import { safeJsonParse } from '@/utils/json';
import { compareVersions } from 'compare-versions';
import { getOr } from 'lodash/fp';
import { DeviceType } from '../SDL.types';

export const getDeviceType = (arg = ''): DeviceType => {
  const SEPARATOR = '-';
  const [, ...deviceType] = arg?.replace(/^dev-/, '')?.split(SEPARATOR) ?? [];

  return deviceType.join(SEPARATOR) as DeviceType;
};

export const getCertification = (arg = '') => {
  const supported = ['Certified', 'Supported by design', 'Tested by partner'];
  const replaceRegex = new RegExp(supported.join('|'), 'i');

  return arg.replace(replaceRegex, 'Supported');
};

const getGroupedReleases = (releases: string[] = []) => {
  const SEPARATOR = ' ';
  const JOIN = '; ';
  const releasesGrouped = releases.reduce((prev: any, curr: string) => {
    if (curr.includes(SEPARATOR)) {
      // Group by version.
      const [version] = curr.split(SEPARATOR);

      return prev[version]
        ? { ...prev, [version]: [...prev[version], curr] }
        : { ...prev, [version]: [curr] };
    }

    // Not grouped firmwares.
    return { ...prev, [curr]: [curr] };
  }, {});

  return Object.values(releasesGrouped).map((grouped: any) => `${grouped.join(JOIN)}${JOIN}`);
};

const getEarlyReleases = (releases: string[] = []) => {
  const [earlyRelease] = releases.reverse();

  return `${earlyRelease}`;
};

const getReleasesStrategy = ({
  releases = [],
  type = 'EARLY',
}: {
  releases?: string[];
  type: 'EARLY' | 'GROUPED' | 'GROUPED_EARLY';
}): string[] | string => {
  if (!releases?.length) return [];
  if (/^EARLY$/.test(type)) return getEarlyReleases(releases);
  if (/^GROUPED$/.test(type)) return getGroupedReleases(releases);
  if (/^GROUPED_EARLY$/.test(type)) return getGroupedReleases(releases).reverse();

  return [];
};

export const getCameraFirmware = (arg = []) => {
  const filteredFirmwares = arg.filter(({ Product = '' }) => /security center/i.test(Product));
  const sortedFirmwares = filteredFirmwares
    ?.map((item: any = {}) => ({
      firmware_version: item?.FirmwareName,
      certification_level: getCertification(item?.Certification || 'N/A'),
      supported_since: getReleasesStrategy({ releases: item?.Releases, type: 'EARLY' }),
    }))
    ?.sort((a, b) => {
      try {
        return compareVersions(a.firmware_version, b.firmware_version);
      } catch (error) {
        return 0;
      }
    })
    ?.reverse();

  return sortedFirmwares;
};

const FirmwareInfoMapperMap = {
  camera: (firmwareInformation: any) => getCameraFirmware(firmwareInformation?.AllFirmwares),
  'access-control': () => [],
  saas: () => [],
  intercom: (firmwareInformation: any) => [
    {
      firmware_version: firmwareInformation?.FirmwareVersion,
      firmware_status: firmwareInformation?.FirmwareStatus,
      supported_since:
        firmwareInformation?.SupportedSince ||
        firmwareInformation?.SecurityCenter?.SecurityCenterInitialRelease ||
        firmwareInformation?.synergisSoftwareVersion ||
        firmwareInformation?.mercuryVersion ||
        '',
    },
  ],
  stratocast: (firmwareInformation: any) => [
    {
      minimum_supported_firmware: firmwareInformation?.MinimalFirmware || 'N/A',
    },
  ],
};

const defaultFirmwareInfoMapper = (firmwareInformation: any) => [
  {
    firmware_version: firmwareInformation?.SecurityCenter?.SecurityCenterLatestFirmware,
    supported_since:
      firmwareInformation?.SupportedSince ||
      firmwareInformation?.SecurityCenter?.SecurityCenterInitialRelease ||
      firmwareInformation?.synergisSoftwareVersion ||
      firmwareInformation?.mercuryVersion ||
      '',
  },
];

export const getFirmwareInfo = ({
  deviceType,
  rawFirmwareInformation = '{}',
}: {
  deviceType: string;
  rawFirmwareInformation: string;
}): any[] => {
  const firmwareInformation = safeJsonParse(rawFirmwareInformation);
  const deviceTypeMapper = getOr(defaultFirmwareInfoMapper, deviceType, FirmwareInfoMapperMap);
  return deviceTypeMapper(firmwareInformation);
};
